import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  CheckCircle2,
  Download,
  FileText,
  ArrowRight,
  ArrowLeft,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Save,
  X,
  Briefcase,
  Heart,
  Brain,
  Users,
  Activity,
  DollarSign,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { assessmentService } from '../services/assessmentService';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language: contextLanguage, setLanguage } = useLanguage();
  const { user, setAuthenticatedUser } = useAuth();

  const result = location.state?.result;
  const userEmail = location.state?.email || '';
  const createdAccount = location.state?.createdAccount || false;
  const passedLanguage = location.state?.language || contextLanguage;
  const userName = location.state?.userName || result?.first_name || '';

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDuplicateEmailModal, setShowDuplicateEmailModal] = useState(false);
  const [accountCreated, setAccountCreated] = useState(createdAccount);
  const [saveEmail, setSaveEmail] = useState(userEmail);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({});

  const texts = {
    en: {
      assessmentComplete: 'Self-Assessment Complete!',
      reportGenerated: 'Your personalized well-being report has been generated',
      wellbeingState: 'Your Well-being State',
      basedOn: 'Based on your responses across 6 key life elements',
      understanding: 'Understanding Your Results:',
      understandingText:
        'This questionnaire provides a snapshot of your current well-being across six key life elements. Remember, there is no "perfect" score, and your well-being naturally fluctuates based on life circumstances. These results are meant to provide awareness and serve as a starting point for reflection and positive change.',
      actionToConsider: 'Action to consider:',
      barDescription: 'This bar represents your overall well-being as you perceived it at the time of questionnaire.',
      important: 'Important to Remember:',
      importantText:
        'This is the cumulative result of all your answers across the 6 elements of well-being. The best results occur by working on all elements simultaneously, not by having a singular focus. Every step towards understanding and improving your well-being is valuable.',
      byElement: 'Your Well-being by Element:',
      clickExpand: 'Click on each element to expand for more details',
      saveTitle: 'Would you like to save your results for future comparison?',
      saveDesc:
        'Create an account to track your progress over time. You can retake the questionnaire later and compare your results to see how your well-being has evolved.',
      saveBtn: 'Save My Results',
      downloadTitle: 'Download Your Report',
      downloadDesc: 'Get your complete 3-page PDF report with detailed insights',
      downloadPdf: 'Download PDF',
      emailSent: 'Email Sent!',
      emailDesc: "We've sent a copy of your report to your email. Check your inbox in the next few minutes.",
      reportAvailable: 'Report available for 60 days',
      whatsNext: "What's Next?",
      step1Title: 'Review Your Full Report',
      step1Desc: 'Download and read through your complete questionnaire with personalized recommendations',
      step2Title: 'Take Action',
      step2Desc: 'Focus on areas that need attention and consider reaching out to a coach or counselor',
      step3Title: 'Retake in 3 Months',
      step3Desc: 'Track your progress by retaking the questionnaire after implementing changes',
      goToDashboard: 'Go To Dashboard',
      returnHome: 'Return to Home',
      needSupport: 'Need support? Contact us at',
      orCall: 'or call',
      saveResultsTitle: 'Save Your Results',
      saveResultsDesc: 'Create a password to save your results and track your progress over time',
      emailAddress: 'Email Address',
      reportSentTo: 'Your report was sent to this email',
      createPassword: 'Create Password',
      confirmPassword: 'Confirm Password',
      atLeast8: 'At least 8 characters',
      reenter: 'Re-enter password',
      cancel: 'Cancel',
      saveResults: 'Save Results',
      checkingEmail: 'Checking email...',
      passwordRequired: 'Password is required',
      passwordLength: 'Password must be at least 8 characters',
      passwordMatch: 'Passwords do not match',
      invalidEmail: 'Please enter a valid email address',
      sessionExpired: 'Session expired. Please complete the questionnaire again.',
      accountCreated: 'Account created! Your results have been saved.',
      noResults: 'No results found.',
      existingEmailTitle: 'Email already used',
      existingEmailMessage: 'An account with this email already exists. Please log in or use a different email.',
      loginBtn: 'Login',
      useDifferentEmail: 'Use different email',
      workCareer: 'Work & Career',
      lovedOnes: 'Loved Ones',
      emotionalMental: 'Emotional & Mental',
      interactionsOthers: 'Interactions With Others',
      physical: 'Physical',
      financial: 'Financial'
    },
    fr: {
      assessmentComplete: 'Auto-évaluation terminé!',
      reportGenerated: 'Votre rapport de bien-être personnalisé a été généré',
      wellbeingState: 'Votre état de bien-être',
      basedOn: 'Basé sur vos réponses à travers 6 éléments clés de la vie',
      understanding: 'Comprendre vos résultats:',
      understandingText:
        'Cette auto-évaluation fournit un aperçu de votre bien-être actuel à travers six éléments clés de la vie. N\'oubliez pas qu\'il n\'y a pas de score "parfait" et que votre bien-être fluctue naturellement en fonction des circonstances de la vie. Ces résultats sont destinés à sensibiliser et à servir de point de départ pour la réflexion et le changement positif.',
      actionToConsider: 'Action à considérer:',
      barDescription: 'Cette barre représente votre bien-être global tel que vous l\'avez perçu au moment de l\'auto-évaluation.',
      important: 'Important à retenir:',
      importantText:
        'Il s\'agit du résultat cumulé de toutes vos réponses aux 6 éléments du bien-être. Les meilleurs résultats sont obtenus en travaillant sur tous les éléments en même temps, et non en se concentrant sur un seul. Chaque étape vers la compréhension et l\'amélioration de votre bien-être est précieuse.',
      byElement: 'Votre bien-être par élément:',
      clickExpand: 'Cliquez sur chaque élément pour plus de détails',
      saveTitle: 'Souhaitez-vous enregistrer vos résultats pour une comparaison future?',
      saveDesc:
        'Créez un compte pour suivre vos progrès au fil du temps. Vous pouvez reprendre l\'auto-évaluation plus tard et comparer vos résultats pour voir comment votre bien-être a évolué.',
      saveBtn: 'Enregistrer mes résultats',
      downloadTitle: 'Télécharger votre rapport',
      downloadDesc: 'Obtenez votre rapport PDF complet de 3 pages avec des informations détaillées',
      downloadPdf: 'Télécharger PDF',
      emailSent: 'Courriel envoyé!',
      emailDesc: 'Nous avons envoyé une copie de votre rapport à votre courriel. Vérifiez votre boîte de réception dans les prochaines minutes.',
      reportAvailable: 'Rapport disponible pendant 60 jours',
      whatsNext: 'Quelle est la suite?',
      step1Title: 'Examiner votre rapport complet',
      step1Desc: 'Téléchargez et lisez votre auto-évaluation complète avec des recommandations personnalisées',
      step2Title: 'Passer à l\'action',
      step2Desc: 'Concentrez-vous sur les domaines qui nécessitent de l\'attention et envisagez de contacter un coach ou un conseiller',
      step3Title: 'Reprendre dans 3 mois',
      step3Desc: 'Suivez vos progrès en reprenant l\'auto-évaluation après avoir mis en œuvre des changements',
      goToDashboard: 'Aller au tableau de bord',
      returnHome: 'Retour à l\'accueil',
      needSupport: 'Besoin d\'aide? Contactez-nous à',
      orCall: 'ou appelez',
      saveResultsTitle: 'Enregistrer vos résultats',
      saveResultsDesc: 'Créez un mot de passe pour enregistrer vos résultats et suivre vos progrès',
      emailAddress: 'Adresse courriel',
      reportSentTo: 'Votre rapport a été envoyé à ce courriel',
      createPassword: 'Créer un mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      atLeast8: 'Au moins 8 caractères',
      reenter: 'Ressaisir le mot de passe',
      cancel: 'Annuler',
      saveResults: 'Enregistrer',
      checkingEmail: 'Vérification du courriel...',
      passwordRequired: 'Mot de passe requis',
      passwordLength: 'Le mot de passe doit contenir au moins 8 caractères',
      passwordMatch: 'Les mots de passe ne correspondent pas',
      invalidEmail: 'Veuillez entrer une adresse courriel valide',
      sessionExpired: 'Session expirée. Veuillez recommencer l\'auto-évaluation.',
      accountCreated: 'Compte créé! Vos résultats ont été enregistrés.',
      noResults: 'Aucun résultat trouvé.',
      existingEmailTitle: 'Courriel déjà utilisé',
      existingEmailMessage: 'Un compte avec ce courriel existe déjà. Veuillez vous connecter ou utiliser un autre courriel.',
      loginBtn: 'Se connecter',
      useDifferentEmail: 'Utiliser un autre courriel',
      workCareer: 'Travail & Carrière',
      lovedOnes: 'Être Cher',
      emotionalMental: 'Émotionnel & Mental',
      interactionsOthers: 'Interaction avec les autres',
      physical: 'Physique',
      financial: 'Financier'
    }
  };

  const txt = texts[passedLanguage] || texts.en;

  const getCopyrightText = () => {
    const yearSuffix = String(new Date().getFullYear()).slice(-2);
    return passedLanguage === 'fr'
      ? `© 2020-${yearSuffix} Centre des points forts, une division de GABCM Inc. Tous droits réservés.`
      : `© 2020-${yearSuffix} Strengths Performance Centre, a division of GABCM Inc. All rights reserved.`;
  };

  const sectionData = {
    work_career: {
      labelKey: 'workCareer',
      icon: Briefcase,
      color: 'bg-blue-100 text-blue-700',
      shortDesc: {
        en: 'Most adults will spend over 54,000 hours at work throughout their 30-year career. As a huge investment of our time, it is important that we actually enjoy what we do each and every day.',
        fr: 'La plupart des adultes passeront plus de 54 000 heures au travail au cours de leur carrière de 30 ans. Comme il s\'agit d\'un investissement considérable de notre temps, il est important que nous aimions ce que nous faisons chaque jour.'
      },
      fullDesc: {
        en: 'Most adults will spend over 54,000 hours at work throughout their 30-year career. As a huge investment of our time, it is important that we actually enjoy what we do each and every day, whether working a regular 9-to-5, as a stay-at-home parent, or taking care of family. A person needs to be mindful of the nature of their work, their environment, their relationship with their manager and peers, and the recognition they receive.',
        fr: 'La plupart des adultes passeront plus de 54 000 heures au travail au cours de leur carrière de 30 ans. Comme il s\'agit d\'un investissement considérable de notre temps, il est important que nous aimions ce que nous faisons chaque jour, que nous travaillions de 9 à 5, en tant que parent au foyer, ou en prenant soin de sa famille. Une personne doit être consciente de la nature de son travail, son environnement, sa relation avec son superviseur et ses pairs, et la reconnaissance qu\'elle reçoit.'
      }
    },
    loved_ones: {
      labelKey: 'lovedOnes',
      icon: Heart,
      color: 'bg-pink-100 text-pink-700',
      shortDesc: {
        en: 'As human beings, we all need to love and be loved, especially by those closest to us. It is important to have positive relationships with those in your immediate circle.',
        fr: 'En tant qu\'êtres humains, nous avons tous besoin d\'aimer et d\'être aimés, surtout par ceux qui nous sont les plus proches. Il est important d\'avoir des relations positives avec ceux de notre cercle immédiat.'
      },
      fullDesc: {
        en: 'As human beings, we all need to love and be loved, especially by those closest to us. Regardless of the definition you give to your loved ones, it is important to have positive relationships with those in your immediate circle: spouse/partner, children, parents, siblings. But beyond our loved ones, it is important to establish and maintain friendships that support us and encourage us in difficult moments, and celebrate with us in good moments.',
        fr: 'Nos êtres chers peuvent inclure les relations, les enfants, la famille et les amis proches que nous considérons comme de la famille. Non seulement le désir d\'aimer et d\'être aimé est considéré comme un besoin humain fondamental, mais nos proches nous aident aussi à nous sentir soutenus et prêts à tout.'
      }
    },
    emotional_mental: {
      labelKey: 'emotionalMental',
      icon: Brain,
      color: 'bg-purple-100 text-purple-700',
      shortDesc: {
        en: 'A person\'s emotional and mental well-being relates to their stress, anxiety, depression, and ability to manage their emotions and keep perspective.',
        fr: 'Le bien-être émotionnel et mental d\'une personne est lié à son stress, son anxiété, sa dépression et sa capacité à gérer ses émotions et à garder une perspective.'
      },
      fullDesc: {
        en: 'A person\'s emotional and mental well-being relates to their stress, anxiety, depression, and ability to manage their emotions. But rather than seeing it as a negative state, it is vital to acknowledge when we are in a bad state and take the necessary actions to get back to a good state. We need to be able to identify our emotional triggers, our mood, our self-talk, and our ability to reframe and keep perspective.',
        fr: 'Les émotions font partie de notre existence humaine naturelle. Être capable d\'identifier nos émotions, comprendre ce qu\'elles sont, ce qui les déclenche, et apprendre à les gérer améliore notre bien-être émotionnel.'
      }
    },
    interactions_others: {
      labelKey: 'interactionsOthers',
      icon: Users,
      color: 'bg-green-100 text-green-700',
      shortDesc: {
        en: 'Your interactions with society and sense of belonging. As human beings, we have an innate need to feel like we are part of something.',
        fr: 'Vos interactions avec la société et votre sentiment d\'appartenance. En tant qu\'êtres humains, nous avons un besoin inné de sentir que nous faisons partie de quelque chose.'
      },
      fullDesc: {
        en: 'Where loved ones are defined as the close personal relationships in your life, "interactions with others" refers to your interactions with society. As human beings, we have an innate need to belong. We need to feel like we are part of something, whether the community in which we live, a club or association, or through social media. Whether introverted or extroverted, we are all social beings.',
        fr: 'Malgré la croyance populaire, les introvertis comme les extravertis ont besoin d\'interagir avec les autres pour s\'épanouir. Tous les humains sont des créatures sociales.'
      }
    },
    physical: {
      labelKey: 'physical',
      icon: Activity,
      color: 'bg-orange-100 text-orange-700',
      shortDesc: {
        en: 'More than just exercise - it encompasses physical activity, sleep, food nutrition, energy, and the link between mental and physical health.',
        fr: 'Plus que l\'exercice - cela englobe l\'activité physique, le sommeil, la nutrition, l\'énergie et le lien entre la santé mentale et physique.'
      },
      fullDesc: {
        en: 'Our physical well-being is more than just getting enough exercise. It encompasses all aspects of our health: physical activity, sleep, food nutrition, and energy. We also tend to acknowledge the intimate link between our mental and physical health, and the impact of stress on our physical well-being. Ultimately, physical well-being relates to our ability to have enough energy in reserve to deal with any challenge we face in life.',
        fr: 'L\'élément de bien-être qui nous est le plus familier et sur lequel nous avons probablement le plus de contrôle est notre bien-être physique. Nos choix de mode de vie et nos habitudes quotidiennes ont une influence majeure sur notre bien-être global.'
      }
    },
    financial: {
      labelKey: 'financial',
      icon: DollarSign,
      color: 'bg-brand-100 text-brand-700',
      shortDesc: {
        en: 'Not about how much money you have, but rather about having enough to deal with the challenges and expectations you have in life.',
        fr: 'Il ne s\'agit pas de combien d\'argent vous avez, mais plutôt d\'en avoir suffisamment pour faire face aux défis et aux attentes de votre vie.'
      },
      fullDesc: {
        en: 'There\'s an old saying that money can\'t buy happiness. That may be true, but money can help make life easier. Your financial well-being is not about how much money you have, but rather about having enough to deal with the challenges and expectations you have. Money is not the root of all evil, but how we value money, our financial health, expectations around money, and our attitude towards money says a lot about our well-being.',
        fr: 'S\'assurer que nos besoins fondamentaux sont satisfaits, prendre en charge nos habitudes de dépenses et de notre budget, se créer un fond de sécurité et planifier notre avenir financier sont tous des facteurs qui peuvent contribuer à influencer cet élément.'
      }
    }
  };

  const isLoggedIn = !!user || !!localStorage.getItem('access_token') || accountCreated;

  const getUserInitials = () => {
    if (user?.first_name) {
      return user.first_name.charAt(0).toUpperCase();
    }
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    if (saveEmail) {
      return saveEmail.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDownloadReport = () => {
    const link = document.createElement('a');
    link.href = result.report_url;
    link.download = '';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailBlur = async () => {
    const trimmedEmail = saveEmail.trim();

    if (!trimmedEmail) {
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setErrors((prev) => ({ ...prev, email: txt.invalidEmail }));
      return;
    }

    setErrors((prev) => ({ ...prev, email: null }));
    setCheckingEmail(true);

    try {
      const response = await assessmentService.checkEmailExists(trimmedEmail);
      if (response?.exists) {
        setShowDuplicateEmailModal(true);
      }
    } catch (error) {
      // Intentionally silent to avoid blocking the flow
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleLoginForExistingEmail = () => {
    navigate('/login', {
      state: {
        prefilledEmail: saveEmail,
        pendingResultsLink: {
          completion_id: result.completion_id,
          result,
          email: saveEmail,
          language: passedLanguage,
          userName
        },
        backTo: '/assessment/results',
        backState: {
          result,
          email: saveEmail,
          createdAccount: false,
          language: passedLanguage,
          userName
        }
      }
    });
  };

  const handleUseDifferentEmail = () => {
    setShowDuplicateEmailModal(false);
  };

  const handleSaveResults = async () => {
    const newErrors = {};

    if (!saveEmail.trim() || !isValidEmail(saveEmail.trim())) {
      newErrors.email = txt.invalidEmail;
    }

    if (!password) {
      newErrors.password = txt.passwordRequired;
    } else if (password.length < 8) {
      newErrors.password = txt.passwordLength;
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = txt.passwordMatch;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!result?.completion_id) {
      toast.error(txt.sessionExpired);
      return;
    }

    setSaving(true);

    try {
      const response = await api.post('/auth/create-account-for-results', {
        email: saveEmail.trim(),
        password,
        completion_id: result.completion_id
      });

      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }

      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setAuthenticatedUser(response.data.user);
      }

      toast.success(txt.accountCreated);
      setShowDuplicateEmailModal(false);
      setShowSaveModal(false);
      setAccountCreated(true);
      setPassword('');
      setConfirmPassword('');
      setErrors({});
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';

      if (errorDetail.includes('already exists')) {
        setShowDuplicateEmailModal(true);
      } else {
        toast.error(
          errorDetail ||
            (passedLanguage === 'fr' ? 'Échec de la création du compte' : 'Failed to create account')
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600 mb-4">{txt.noResults}</p>
            <Button onClick={() => navigate('/')} className="w-full">
              {txt.returnHome}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showSaveCard = !isLoggedIn;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-brand-50/20 to-white">
      {isLoggedIn && (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex justify-between items-center">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-brand-600 hover:text-brand-700 hover:bg-brand-50"
                data-testid="go-to-dashboard-btn"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {txt.goToDashboard}
              </Button>

              <div className="flex items-center gap-3">
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                      contextLanguage === 'en'
                        ? 'bg-brand-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid="lang-en-btn-results"
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage('fr')}
                    className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                      contextLanguage === 'fr'
                        ? 'bg-brand-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid="lang-fr-btn-results"
                  >
                    FR
                  </button>
                </div>

                <div className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-bold">
                  {getUserInitials()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-100 rounded-full mb-4">
              <CheckCircle2 className="w-12 h-12 text-brand-600" />
            </div>
            <h1 className="text-4xl font-black text-gray-900 mb-2">{txt.assessmentComplete}</h1>
            <p className="text-lg text-gray-600">{txt.reportGenerated}</p>
          </div>

          <Card className="shadow-xl border-gray-200 mb-6">
            <CardHeader className="bg-gradient-to-br from-brand-50 to-white border-b border-gray-200">
              <CardTitle className="text-2xl font-black text-gray-900">{txt.wellbeingState}</CardTitle>
              <CardDescription>{txt.basedOn}</CardDescription>
            </CardHeader>

            <CardContent className="pt-8 space-y-8">
              <div className="bg-brand-50 border-l-4 border-brand-600 p-6 rounded-r-lg">
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">{txt.understanding}</strong> {txt.understandingText}
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="space-y-2">
                  <div className="text-4xl font-black text-brand-600">{result.overall_label}</div>
                  <div className="text-xl font-semibold text-gray-700">
                    {txt.actionToConsider} <span className="text-brand-600">{result.overall_action_phrase}</span>
                  </div>
                </div>

                <div className="max-w-md mx-auto">
                  <div className="w-full h-10 bg-gray-200 border border-gray-300 overflow-hidden">
                    <div
                      className="h-full bg-brand-600 transition-all duration-1000"
                      style={{ width: `${(result.overall_score / 180) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-3 italic">{txt.barDescription}</p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">{txt.important}</strong> {txt.importantText}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{txt.byElement}</h3>
                <p className="text-sm text-gray-500 mb-2">{txt.clickExpand}</p>
                <div className="grid grid-cols-1 gap-4">
                  {Object.entries(result.section_scores).map(([key, data]) => {
                    const section = sectionData[key];
                    if (!section) return null;

                    const IconComponent = section.icon;
                    const isExpanded = expandedSections[key];

                    return (
                      <div key={key} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => toggleSection(key)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${section.color}`}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div className="font-bold text-gray-900 text-base">{txt[section.labelKey]}</div>
                          </div>
                          <button type="button" className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500" />
                            )}
                          </button>
                        </div>

                        <div className="w-full h-8 bg-gray-200 border border-gray-300 overflow-hidden my-3">
                          <div className="h-full bg-brand-600" style={{ width: `${data.percentage}%` }} />
                        </div>

                        <p className="text-sm text-gray-600 leading-relaxed">{section.shortDesc[passedLanguage]}</p>
                        {isExpanded && (
                          <p className="text-sm text-gray-600 leading-relaxed mt-3">{section.fullDesc[passedLanguage]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {showSaveCard && (
            <Card className="shadow-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white mb-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Save className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{txt.saveTitle}</h3>
                    <p className="text-sm text-gray-600 mb-4">{txt.saveDesc}</p>
                    <Button
                      onClick={() => setShowSaveModal(true)}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="save-results-btn"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {txt.saveBtn}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="shadow-lg hover:shadow-xl transition-shadow cursor-pointer" onClick={handleDownloadReport}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-brand-100 rounded-lg">
                    <Download className="h-6 w-6 text-brand-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{txt.downloadTitle}</h3>
                    <p className="text-sm text-gray-600 mb-3">{txt.downloadDesc}</p>
                    <Button variant="outline" className="w-full" data-testid="download-report-btn">
                      <FileText className="mr-2 h-4 w-4" />
                      {txt.downloadPdf}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{txt.emailSent}</h3>
                    <p className="text-sm text-gray-600">{txt.emailDesc}</p>
                    <p className="text-xs text-gray-500 mt-2">{txt.reportAvailable}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-xl border-2 border-brand-200 bg-gradient-to-br from-brand-50 to-white">
            <CardHeader>
              <CardTitle className="text-xl font-black text-gray-900">{txt.whatsNext}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{txt.step1Title}</h4>
                    <p className="text-sm text-gray-600">{txt.step1Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{txt.step2Title}</h4>
                    <p className="text-sm text-gray-600">{txt.step2Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{txt.step3Title}</h4>
                    <p className="text-sm text-gray-600">{txt.step3Desc}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-brand-200">
                <Button
                  onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}
                  className="w-full bg-brand-600 hover:bg-brand-700"
                  size="lg"
                  data-testid="return-home-btn"
                >
                  {isLoggedIn ? txt.goToDashboard : txt.returnHome}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-gray-600 text-sm">
            <p>
              {txt.needSupport}{' '}
              <a
                href={passedLanguage === 'fr' ? 'mailto:bien-etre@spc-cpf.com' : 'mailto:wellbeing@spc-cpf.com'}
                className="text-brand-600 hover:underline"
              >
                {passedLanguage === 'fr' ? 'bien-etre@spc-cpf.com' : 'wellbeing@spc-cpf.com'}
              </a>{' '}
              {txt.orCall} +1 (613) 421-4722 ext.5
            </p>
          </div>

          <div className="mt-6 text-center text-xs text-gray-500">
            <p>{getCopyrightText()}</p>
          </div>
        </div>
      </div>

      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md shadow-2xl">
            <CardHeader className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowSaveModal(false);
                  setShowDuplicateEmailModal(false);
                }}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
              <CardTitle className="text-xl font-black text-gray-900">{txt.saveResultsTitle}</CardTitle>
              <CardDescription>{txt.saveResultsDesc}</CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="save-email">{txt.emailAddress}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="save-email"
                    type="email"
                    value={saveEmail}
                    onChange={(e) => {
                      setSaveEmail(e.target.value);
                      if (errors.email) {
                        setErrors((prev) => ({ ...prev, email: null }));
                      }
                    }}
                    onBlur={handleEmailBlur}
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="john.doe@example.com"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                {checkingEmail && <p className="text-xs text-gray-500">{txt.checkingEmail}</p>}
                <p className="text-xs text-gray-500">{txt.reportSentTo}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="save-password">{txt.createPassword}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="save-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors((prev) => ({ ...prev, password: null }));
                      }
                    }}
                    className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                    placeholder={txt.atLeast8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="save-confirm-password">{txt.confirmPassword}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="save-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors((prev) => ({ ...prev, confirmPassword: null }));
                      }
                    }}
                    className={`pl-10 pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    placeholder={txt.reenter}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
              </div>

              <div className="pt-4 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSaveModal(false);
                    setShowDuplicateEmailModal(false);
                  }}
                  className="flex-1"
                >
                  {txt.cancel}
                </Button>
                <Button
                  onClick={handleSaveResults}
                  disabled={saving || checkingEmail}
                  className="flex-1 bg-brand-600 hover:bg-brand-700"
                >
                  {saving ? '...' : txt.saveResults}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showDuplicateEmailModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <Card className="w-full max-w-md shadow-2xl">
            <CardHeader className="relative">
              <button
                type="button"
                onClick={() => setShowDuplicateEmailModal(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
              <CardTitle className="text-xl font-black text-gray-900">{txt.existingEmailTitle}</CardTitle>
              <CardDescription>{txt.existingEmailMessage}</CardDescription>
            </CardHeader>

            <CardContent className="pt-2">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleUseDifferentEmail}
                >
                  {txt.useDifferentEmail}
                </Button>
                <Button
                  className="flex-1 bg-brand-600 hover:bg-brand-700"
                  onClick={handleLoginForExistingEmail}
                >
                  {txt.loginBtn}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ResultsPage;
